import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, X, Store, RefreshCw } from "lucide-react";
import { EmailSendingCard } from "@/components/settings/EmailSendingCard";
import { StartFreshCard } from "@/components/settings/StartFreshCard";

export default function SettingsPage() {
  const [categories, setCategories] = useState([]);
  const [newCat, setNewCat] = useState("");
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [savingConfig, setSavingConfig] = useState(false);
  const [shopify, setShopify] = useState(null);
  const [shopifyForm, setShopifyForm] = useState({ store_domain: "", access_token: "", auto_sync: true });
  const [shopifyBusy, setShopifyBusy] = useState(false);

  const loadShopify = () => {
    api.get("/shopify/settings").then((r) => {
      setShopify(r.data);
      setShopifyForm((f) => ({ ...f, store_domain: r.data.store_domain || f.store_domain, auto_sync: r.data.auto_sync }));
    }).catch(() => {});
  };

  useEffect(() => {
    api.get("/settings/categories").then((r) => setCategories(r.data.values)).catch((e) => toast.error(apiError(e)));
    api.get("/settings/statement").then((r) => setConfig(r.data)).catch(() => {});
    loadShopify();
  }, []);

  const saveShopify = async () => {
    setShopifyBusy(true);
    try {
      const { data } = await api.put("/shopify/settings", shopifyForm);
      setShopify(data);
      setShopifyForm((f) => ({ ...f, access_token: "" }));
      toast.success("Shopify connection saved. Click \"Test connection\" to make sure it works.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setShopifyBusy(false);
    }
  };

  const testShopify = async () => {
    setShopifyBusy(true);
    try {
      const { data } = await api.post("/shopify/test");
      toast.success(`Connected! Shopify says hello from "${data.shop_name}".`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setShopifyBusy(false);
    }
  };

  const syncShopifyNow = async () => {
    setShopifyBusy(true);
    try {
      const { data } = await api.post("/shopify/sync");
      toast.success(data.message);
      setTimeout(loadShopify, 5000);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setShopifyBusy(false);
    }
  };

  const disconnectShopify = async () => {
    try {
      await api.delete("/shopify/settings");
      toast.success("Shopify disconnected. Automatic syncing is off.");
      setShopify(null);
      setShopifyForm({ store_domain: "", access_token: "", auto_sync: true });
      loadShopify();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      const { data } = await api.put("/settings/statement", config);
      setConfig(data);
      toast.success("Statement details saved.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSavingConfig(false);
    }
  };

  const toggleDeductible = (cat) => {
    const set = new Set(config.deductible_categories || []);
    if (set.has(cat)) set.delete(cat); else set.add(cat);
    setConfig({ ...config, deductible_categories: [...set] });
  };

  const add = () => {
    const c = newCat.trim();
    if (!c) return;
    if (categories.includes(c)) { toast.error("That category already exists."); return; }
    setCategories([...categories, c]);
    setNewCat("");
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/categories", { values: categories });
      setCategories(data.values);
      toast.success("Categories saved.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8 max-w-3xl" data-testid="settings-page">
      <div>
        <h1 className="text-4xl sm:text-5xl font-serif font-semibold">Settings</h1>
        <p className="text-muted-foreground mt-2">Options that shape how orders are organized.</p>
      </div>

      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="font-serif text-2xl">Product categories</CardTitle>
          <p className="text-sm text-muted-foreground">Used to group sales in reports. "Other" is always kept as a catch-all.</p>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex flex-wrap gap-2" data-testid="categories-list">
            {categories.map((c) => (
              <Badge key={c} variant="secondary" className="text-sm py-1.5 px-3 gap-2" data-testid={`category-badge-${c}`}>
                {c}
                {c !== "Other" && (
                  <button onClick={() => setCategories(categories.filter((x) => x !== c))} aria-label={`Remove ${c}`} data-testid={`remove-category-${c}`}>
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </Badge>
            ))}
          </div>
          <div className="flex items-end gap-2">
            <div className="space-y-1.5 flex-1">
              <Label>Add a category</Label>
              <Input value={newCat} onChange={(e) => setNewCat(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="e.g. Music" data-testid="new-category-input" />
            </div>
            <Button variant="secondary" onClick={add} data-testid="add-category-btn"><Plus className="h-4 w-4" /></Button>
          </div>
          <Button onClick={save} disabled={saving} data-testid="save-categories-btn">{saving ? "Saving…" : "Save categories"}</Button>
        </CardContent>
      </Card>

      {config && (
        <Card className="shadow-sm" data-testid="statement-settings-card">
          <CardHeader>
            <CardTitle className="font-serif text-2xl">Statement & organization details</CardTitle>
            <p className="text-sm text-muted-foreground">Shown on year-end PDF statements. The disclosure wording can be edited by your CPA.</p>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Ministry name</Label>
                <Input value={config.org_name} onChange={(e) => setConfig({ ...config, org_name: e.target.value })} data-testid="org-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label>EIN (optional)</Label>
                <Input value={config.ein} onChange={(e) => setConfig({ ...config, ein: e.target.value })} placeholder="12-3456789" data-testid="org-ein-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Mailing address</Label>
              <Textarea rows={2} value={config.org_address} onChange={(e) => setConfig({ ...config, org_address: e.target.value })} data-testid="org-address-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Disclosure wording (goods-or-services language)</Label>
              <Textarea rows={4} value={config.disclosure} onChange={(e) => setConfig({ ...config, disclosure: e.target.value })} data-testid="disclosure-input" />
            </div>
            <div className="space-y-2">
              <Label>Which categories count as tax-deductible contributions?</Label>
              <div className="flex flex-wrap gap-4">
                {categories.map((c) => (
                  <label key={c} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={(config.deductible_categories || []).includes(c)}
                      onCheckedChange={() => toggleDeductible(c)}
                      data-testid={`deductible-${c}`}
                    />
                    {c}
                  </label>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">Everything else appears under "Purchases" on the statement and is not deductible.</p>
            </div>
            <Button onClick={saveConfig} disabled={savingConfig} data-testid="save-statement-settings-btn">
              {savingConfig ? "Saving…" : "Save statement details"}
            </Button>
          </CardContent>
        </Card>
      )}

      <EmailSendingCard />
      <Card className="shadow-sm" data-testid="shopify-settings-card">
        <CardHeader>
          <CardTitle className="font-serif text-2xl flex items-center gap-2"><Store className="h-5 w-5 text-primary" /> Shopify auto-sync</CardTitle>
          <p className="text-sm text-muted-foreground">
            Connect your store once and new orders are pulled in automatically every 30 minutes — no more file uploads.
            Synced orders appear in Import History and can be undone like any import.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {shopify?.connected ? (
            <div className="rounded-md bg-secondary/50 px-4 py-3 text-sm space-y-1" data-testid="shopify-status">
              <p>
                <span className="inline-block px-2.5 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium mr-2">Connected</span>
                <strong>{shopify.store_domain}</strong> · token {shopify.token_hint}
              </p>
              <p className="text-muted-foreground">
                {shopify.last_synced_at ? `Last checked ${new Date(shopify.last_synced_at).toLocaleString()}` : "Not synced yet"}
                {shopify.last_sync_result ? ` — ${shopify.last_sync_result}` : ""}
              </p>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground" data-testid="shopify-help">
              To get your token: in Shopify admin go to <strong>Settings → Apps and sales channels → Develop apps → Create an app</strong>,
              give it the <strong>read_orders</strong> permission, install it, and copy the Admin API access token (it starts with shpat_).
            </p>
          )}
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Store address</Label>
              <Input placeholder="yourstore.myshopify.com" value={shopifyForm.store_domain}
                onChange={(e) => setShopifyForm({ ...shopifyForm, store_domain: e.target.value })}
                data-testid="shopify-domain-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Admin API access token {shopify?.connected ? "(leave blank to keep the current one)" : ""}</Label>
              <Input type="password" placeholder="shpat_…" value={shopifyForm.access_token}
                onChange={(e) => setShopifyForm({ ...shopifyForm, access_token: e.target.value })}
                data-testid="shopify-token-input" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox checked={shopifyForm.auto_sync}
              onCheckedChange={(v) => setShopifyForm({ ...shopifyForm, auto_sync: !!v })}
              data-testid="shopify-autosync-checkbox" />
            Automatically pull new orders every 30 minutes
          </label>
          <div className="flex flex-wrap gap-3">
            <Button onClick={saveShopify} disabled={shopifyBusy} data-testid="shopify-save-btn">Save connection</Button>
            <Button variant="secondary" onClick={testShopify} disabled={shopifyBusy || !shopify?.connected} data-testid="shopify-test-btn">
              Test connection
            </Button>
            <Button variant="secondary" onClick={syncShopifyNow} disabled={shopifyBusy || !shopify?.connected} data-testid="shopify-sync-now-btn">
              <RefreshCw className="h-4 w-4 mr-2" /> Sync now
            </Button>
            {shopify?.connected && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="ghost" className="text-destructive hover:text-destructive" data-testid="shopify-disconnect-btn">Disconnect</Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle className="font-serif">Disconnect Shopify?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Automatic syncing will stop. Orders already imported stay exactly as they are, and you can still upload CSV files by hand.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Stay connected</AlertDialogCancel>
                    <AlertDialogAction onClick={disconnectShopify} className="bg-destructive hover:bg-destructive/90" data-testid="confirm-disconnect-btn">
                      Yes, disconnect
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </CardContent>
      </Card>
      <StartFreshCard />
    </div>
  );
}
